import type {
  FetchVatDefinitionListParams,
  VATDefinition
} from 'Services/ProductManagement/Features/VATDefinition/types'
import { createBaseApi } from 'Util/ApiClient'
import { formatToTimeZoneAwareISO } from 'Util/formatters'

enum TagTypes {
  VATDefinition = 'VATDefinition',
  ActiveVATDefinition = 'ActiveVATDefinition'
}

export const vatDefinitionApi = createBaseApi({
  reducerPath: 'vatDefinitionApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.VATDefinition, TagTypes.ActiveVATDefinition]
}).injectEndpoints({
  endpoints: builder => ({
    getVatDefinitions: builder.query<VATDefinition[], FetchVatDefinitionListParams | undefined>({
      query: params => ({
        url: '/api/v1/vat-definition/',
        params
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.VATDefinition, id }) as const),
              { type: TagTypes.VATDefinition, id: 'LIST' }
            ]
          : [{ type: TagTypes.VATDefinition, id: 'LIST' }],
      transformResponse: (response: { resources: VATDefinition[] }) => response.resources ?? []
    }),
    getActiveVatDefinitions: builder.query<
      VATDefinition[],
      FetchVatDefinitionListParams | undefined
    >({
      query: params => {
        const currentDateTime = formatToTimeZoneAwareISO(new Date())
        return {
          url: '/api/v1/vat-definition/',
          params: {
            ...params,
            filter: `activeStart le "${currentDateTime}" and activeEnd ge "${currentDateTime}"`
          }
        }
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.ActiveVATDefinition, id }) as const),
              { type: TagTypes.ActiveVATDefinition, id: 'LIST' }
            ]
          : [{ type: TagTypes.ActiveVATDefinition, id: 'LIST' }],
      transformResponse: (response: { resources: VATDefinition[] }) => response.resources
    }),
    getSingleVatDefinition: builder.query<VATDefinition, string>({
      query: id => `/api/v1/vat-definition/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.VATDefinition, id }]
    }),
    createSingleVatDefinition: builder.mutation<VATDefinition, Partial<VATDefinition>>({
      query: data => ({
        url: `/api/v1/vat-definition/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.VATDefinition, id: 'LIST' },
        { type: TagTypes.ActiveVATDefinition, id: 'LIST' }
      ]
    }),
    updateSingleVatDefinition: builder.mutation<
      VATDefinition,
      { id: string } & Partial<VATDefinition>
    >({
      query: data => {
        const { id, ...fields } = data
        return {
          url: `/api/v1/vat-definition/${id}/`,
          method: 'PUT',
          body: fields
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.VATDefinition, id },
        { type: TagTypes.ActiveVATDefinition, id }
      ]
    }),
    deleteSingleVatDefinition: builder.mutation<Record<string, never>, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/vat-definition/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: () => [
        { type: TagTypes.VATDefinition, id: 'LIST' },
        { type: TagTypes.ActiveVATDefinition, id: 'LIST' }
      ]
    })
  })
})

export const {
  useGetVatDefinitionsQuery,
  useGetActiveVatDefinitionsQuery,
  useCreateSingleVatDefinitionMutation,
  useUpdateSingleVatDefinitionMutation,
  useDeleteSingleVatDefinitionMutation,
  useGetSingleVatDefinitionQuery
} = vatDefinitionApi
